import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FiCalendar, FiFolder, FiRefreshCw} from 'react-icons/fi';
import {HiOutlineSearch, HiOutlineX} from 'react-icons/hi';
import {useOrganizationService, useAzureService} from '../../services';
import useJiraService from '../../services/jira';
import { SingleSelect } from '../common/Search';
import { useKickoffAndPoll } from '../../hooks/useKickoffAndPoll';
import { useDispatch, useSelector } from 'react-redux';
import { setAzureStatesSyncRunning, setJiraStatesSyncRunning } from '../../redux/slices/organization';

export interface SprintsListProps {
  defaultType?: 'azure' | 'jira';
}

interface Sprint {
  id: number;
  sprint_id: string;
  name: string;
  path: string;
  start_date: string;
  end_date: string;
  status: string;
  project_id: string;
  project_name: string;
  team_id: string;
  team_name: string;
}

const statusOptions = [
  { label: 'All', value: '' },
  { label: 'Current', value: 'current' },
  { label: 'Past', value: 'past' },
  { label: 'Future', value: 'future' },
];

const statusColors: Record<string, string> = {
  current: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border-blue-200 dark:border-blue-700',
  past: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-400 border-gray-200 dark:border-gray-700',
  future: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 border-green-200 dark:border-green-700',
};

export default function SprintsList({ defaultType = 'azure' }: SprintsListProps) {
  const dispatch = useDispatch();
  const { fetchSprints } = useOrganizationService();
  const { refreshAzureStatusStages } = useAzureService();
  const { refreshJiraStatusStages } = useJiraService();

  const {
    azureStatesSyncRunning: isRefreshingAzure,
    jiraStatesSyncRunning: isRefreshingJira
  } = useSelector((state: any) => state.organization);
  
  const { start: startAzure } = useKickoffAndPoll({
    kickoff: () => refreshAzureStatusStages(false, false),
    check: () => refreshAzureStatusStages(true, true),
    onKickoff: () => {},
    onComplete: () => {
      dispatch(setAzureStatesSyncRunning(false));
      loadSprints();
    },
    options: {
      intervalMs: 1000,
      maxWaitMs: 5 * 60 * 1000,
      persistAcrossUnmount: true,
      onStatusChange: (running) => dispatch(setAzureStatesSyncRunning(running))
    }
  });

  const { start: startJira } = useKickoffAndPoll({
    kickoff: () => refreshJiraStatusStages(false, false),
    check: () => refreshJiraStatusStages(true, true),
    onKickoff: () => {},
    onComplete: () => {
      dispatch(setJiraStatesSyncRunning(false));
      loadSprints();
    },
    options: {
      intervalMs: 1000,
      maxWaitMs: 5 * 60 * 1000,
      persistAcrossUnmount: true,
      onStatusChange: (running) => dispatch(setJiraStatesSyncRunning(running))
    }
  });

  const [search, setSearch] = useState('');
  const [selectedType, setSelectedType] = useState(defaultType);
  const [selectedStatus, setSelectedStatus] = useState('current');
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Navigation state
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [currentTeamIndex, setCurrentTeamIndex] = useState(0);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const teamScrollContainerRef = useRef<HTMLDivElement>(null);

  const isRefreshing = selectedType === 'azure' ? isRefreshingAzure : isRefreshingJira;

  useEffect(() => {
    loadSprints();
  }, [selectedType, selectedStatus]);

  useEffect(() => {
    setSelectedType(defaultType);
  }, [defaultType]);

  const loadSprints = async () => {
    try {
      setIsLoading(true);
      const sprintsData = await fetchSprints(selectedType, selectedStatus || undefined);
      setSprints(sprintsData);
    } catch (error) {
      console.error('Error loading sprints:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      if (selectedType === 'azure') {
        await startAzure();
      } else if (selectedType === 'jira') {
        await startJira();
      }
    } catch (error) {
      console.error("Sync failed:", error);
      if (selectedType === 'azure') {
        dispatch(setAzureStatesSyncRunning(false));
      } else if (selectedType === 'jira') {
        dispatch(setJiraStatesSyncRunning(false));
      }
    }
  };

  const filteredSprints = useMemo(() => {
    if (!search.trim()) return sprints;

    const lowerSearch = search.toLowerCase();

    const matchedProjects = new Set(
      sprints.filter(s => s.project_name.toLowerCase().includes(lowerSearch)).map(s => s.project_name)
    );

    const matchedTeams = new Set(
      sprints.filter(s => s.team_name.toLowerCase().includes(lowerSearch)).map(s => s.team_name)
    );

    return sprints.filter(s =>
      s.name.toLowerCase().includes(lowerSearch) ||
      matchedProjects.has(s.project_name) ||
      matchedTeams.has(s.team_name)
    );
  }, [sprints, search]);

  const groupedData = useMemo(() => {
    const grouped: Record<string, Record<string, Sprint[]>> = {};

    // Group all sprints by project and team (from full sprints list)
    const allProjectTeamMap: Record<string, Record<string, Sprint[]>> = {};
    sprints.forEach(sprint => {
      if (!allProjectTeamMap[sprint.project_name]) {
        allProjectTeamMap[sprint.project_name] = {};
      }
      if (!allProjectTeamMap[sprint.project_name][sprint.team_name]) {
        allProjectTeamMap[sprint.project_name][sprint.team_name] = [];
      }
      allProjectTeamMap[sprint.project_name][sprint.team_name].push(sprint);
    });

    // Build final groupedData from filteredSprints
    const matchedProjectNames = new Set(filteredSprints.map(s => s.project_name));

    matchedProjectNames.forEach(projectName => {
      const teams = allProjectTeamMap[projectName];
      if (!teams) return;

      grouped[projectName] = {};

      Object.entries(teams).forEach(([teamName, teamSprints]) => {
        // Filter down to only sprints that are in filteredSprints
        const matchedSprints = teamSprints.filter(s =>
          filteredSprints.some(fs => fs.id === s.id)
        );
        grouped[projectName][teamName] = matchedSprints;
      });
    });

    return grouped;
  }, [sprints, filteredSprints]);

  const allProjectTeamMap = useMemo(() => {
    const map: Record<string, Record<string, Sprint[]>> = {};
    sprints.forEach(sprint => {
      if (!map[sprint.project_name]) map[sprint.project_name] = {};
      if (!map[sprint.project_name][sprint.team_name]) map[sprint.project_name][sprint.team_name] = [];
      map[sprint.project_name][sprint.team_name].push(sprint);
    });
    return map;
  }, [sprints]);

  const projectKeys = Object.keys(groupedData).sort();
  const currentProjectKey = projectKeys[currentProjectIndex];
  const currentProject = currentProjectKey ? groupedData[currentProjectKey] : {};

  const teamKeys = Object.keys(currentProject).sort();
  const currentTeamKey = teamKeys[currentTeamIndex];

  const currentSprints = currentProjectKey && currentTeamKey
    ? (allProjectTeamMap[currentProjectKey]?.[currentTeamKey] || [])
    : [];

  useEffect(() => {
    const query = search.trim().toLowerCase();
    if (!query) return;

    let projectIndexToUse = projectKeys.findIndex(project =>
      project.toLowerCase().includes(query)
    );

    if (projectIndexToUse === -1) {
      projectIndexToUse = projectKeys.findIndex(project =>
        Object.keys(groupedData[project] || {}).some(team => team.toLowerCase().includes(query))
      );
    }

    if (projectIndexToUse === -1) projectIndexToUse = currentProjectIndex;

    const projKey = projectKeys[projectIndexToUse];
    if (!projKey) return;

    const teams = Object.keys(groupedData[projKey] || {});
    const matchedTeam = teams.find(team => team.toLowerCase().includes(query));
    const sortedTeams = teams.slice().sort();
    const matchedTeamIndex = matchedTeam ? sortedTeams.indexOf(matchedTeam) : 0;

    if (projectIndexToUse !== currentProjectIndex) setCurrentProjectIndex(projectIndexToUse);
    if (matchedTeamIndex !== currentTeamIndex) setCurrentTeamIndex(matchedTeamIndex);
  }, [search, projectKeys.join(','), groupedData]);

  useEffect(() => {
    if (!search.trim()) {
      setCurrentProjectIndex(0);
      setCurrentTeamIndex(0);
    }
  }, [search.trim() === '' ? '' : 'x']);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const getStatusDisplay = (status: string) => {
    const statusMap: Record<string, string> = {
      current: 'Current',
      past: 'Past',
      future: 'Future'
    };
    return statusMap[status] || status;
  };

  // Navigation handlers
  const handlePreviousProject = () => {
    if (currentProjectIndex > 0) {
      const nextIndex = currentProjectIndex - 1;
      setCurrentProjectIndex(nextIndex);
      const query = search.trim().toLowerCase();
      if (query) {
        const projKey = projectKeys[nextIndex];
        const teams = Object.keys(groupedData[projKey] || {}).slice().sort();
        const matchIdx = teams.findIndex(t => t.toLowerCase().includes(query));
        setCurrentTeamIndex(matchIdx !== -1 ? matchIdx : 0);
      }
    }
  };

  const handleNextProject = () => {
    if (currentProjectIndex < projectKeys.length - 1) {
      const nextIndex = currentProjectIndex + 1;
      setCurrentProjectIndex(nextIndex);
      const query = search.trim().toLowerCase();
      if (query) {
        const projKey = projectKeys[nextIndex];
        const teams = Object.keys(groupedData[projKey] || {}).slice().sort();
        const matchIdx = teams.findIndex(t => t.toLowerCase().includes(query));
        setCurrentTeamIndex(matchIdx !== -1 ? matchIdx : 0);
      }
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleClearSearch = () => {
    setSearch('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(e.target.value);
  };

  // Auto-scroll to selected team
  useEffect(() => {
    if (teamScrollContainerRef.current) {
      const selectedTeamElement = document.getElementById(`team-${currentTeamIndex}`);
      if (selectedTeamElement) {
        const container = teamScrollContainerRef.current;
        const containerWidth = container.offsetWidth;
        const teamElementLeft = selectedTeamElement.offsetLeft;
        const teamElementWidth = selectedTeamElement.offsetWidth;
        
        const scrollPosition = teamElementLeft - (containerWidth / 2) + (teamElementWidth / 2);
        
        container.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  }, [currentTeamIndex]);

  // Skeleton Loader Component
  const SkeletonLoader = () => (
    <>
      {/* Top Message Skeleton */}
      <div className="mb-4 flex items-center justify-between bg-light-status-info-bg dark:bg-blue-900/30 p-4 rounded-lg border border-light-status-info-border dark:border-blue-600">
        <div>
          <div className="w-32 h-4 bg-blue-200 dark:bg-blue-700 rounded mb-2 animate-pulse"></div>
          <div className="w-48 h-3 bg-blue-200 dark:bg-blue-700 rounded animate-pulse"></div>
        </div>
        <div className="w-24 h-8 bg-blue-200 dark:bg-blue-700 rounded-lg animate-pulse"></div>
      </div>

      <div className="mt-6 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        {/* Top Section Skeleton */}
        <div className="bg-gradient-to-r from-tertiary-light to-secondary-light dark:from-tertiary-dark dark:to-secondary-dark px-6 py-4 border-b border-gray-100 dark:border-gray-700 h-20">
          <div className="flex items-center justify-between h-full">
            {/* Left - Search Skeleton */}
            <div className="flex items-center  w-40">
              <div className="flex items-center bg-secondary-light dark:bg-secondary-dark border border-gray-200 dark:border-gray-600 rounded-lg">
                <div className="flex items-center px-3 py-2">
                  <div className="w-4 h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                </div>
                <div className="px-2 py-2 w-32">
                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Center - Project Navigation Skeleton */}
            <div className="flex items-center justify-center flex-1">
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-3 bg-gray-300 dark:bg-gray-600 rounded mb-2 animate-pulse"></div>
                <div className="w-32 h-6 bg-gray-300 dark:bg-gray-600 rounded mb-1 animate-pulse"></div>
                
                {/* Compact Navigation with Pagination Skeleton */}
                <div className="flex items-center justify-center space-x-1">
                  <div className="w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded-md animate-pulse"></div>
                  <div className="w-16 h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
                  <div className="w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded-md animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Right - Status Filter Skeleton */}
            <div className="flex items-center justify-end w-40">
              <div className="w-32 h-10 bg-gray-300 dark:bg-gray-600 rounded-lg animate-pulse"></div>
            </div>
          </div>
        </div>

      {/* Team Selection Skeleton */}
      <div className="bg-gradient-to-r from-hover-light to-tertiary-light dark:from-hover-dark dark:to-tertiary-dark p-4 border-b border-gray-100 dark:border-gray-700">
        <div className="flex flex-col">
          <div className="w-12 h-3 bg-gray-300 dark:bg-gray-600 rounded mb-2 animate-pulse"></div>
          <div className="flex items-center space-x-2">
            {[1, 2, 3, 4].map((index) => (
              <div 
                key={index}
                className="w-20 h-8 bg-gray-300 dark:bg-gray-600 rounded-md animate-pulse"
              />
            ))}
          </div>
        </div>
      </div>

      {/* Sprints Content Skeleton */}
      <div className="p-5 bg-secondary-light dark:bg-secondary-dark">
        <div className="flex items-center justify-between mb-4">
          <div className="w-32 h-6 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
          <div className="w-40 h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
        </div>
        
        <div className="space-y-3 min-h-[280px] max-h-[280px]">
          {[1, 2, 3, 4].map((index) => (
            <div 
              key={index}
              className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-primary-light/5 to-hover-light dark:from-primary-dark/5 dark:to-hover-dark border border-primary-light/10 dark:border-gray-700"
            >
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 bg-gray-300 dark:bg-gray-600 rounded-full animate-pulse"></div>
                <div className="w-24 h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
              </div>
              <div className="flex items-center">
                <div className="w-16 h-8 bg-gray-300 dark:bg-gray-600 rounded-md mr-1 animate-pulse"></div>
                <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-lg animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    </>
  );

  // Show skeleton loader when loading
  if (isLoading) {
    return <SkeletonLoader />;
  }

  return (
    <>
      {/* Sprint info header - same style as Workflow State Configuration */}
      <div className="mb-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-light-status-info-bg dark:bg-blue-900/30 p-4 rounded-lg border border-light-status-info-border dark:border-blue-600">
        <div className="flex-1">
          <h3 className="text-sm font-medium text-light-status-info-text dark:text-blue-300 mb-1">
            Sprint Synchronization
          </h3>
          <p className="text-sm text-light-text-secondary dark:text-gray-400">
            {selectedType === 'jira'
              ? 'Sync your Jira sprints to keep iteration timelines aligned with CadencePro. This pulls current, past, and future sprints for all imported projects.'
              : 'Sync your Azure DevOps iterations to keep sprint timelines aligned with CadencePro. This pulls current, past, and future sprints for all imported projects.'}
          </p>
        </div>
        <div className="flex-shrink-0">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-light hover:text-primary-hover dark:text-primary-dark dark:hover:text-primary-light border border-primary-light hover:border-primary-hover dark:border-primary-dark dark:hover:border-primary-light bg-secondary-light dark:bg-secondary-dark rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-sm"
          >
            <FiRefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Syncing...' : 'Sync'}
          </button>
        </div>
      </div>

      <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden min-h-[600px] flex flex-col">
        {/* Top Section - Fixed height with consistent layout */}
        <div className="bg-gradient-to-r from-tertiary-light to-secondary-light dark:from-tertiary-dark dark:to-secondary-dark px-6 py-4 border-b border-gray-100 dark:border-gray-700 h-20 flex-shrink-0">
          <div className="flex items-center justify-between h-full">
            {/* Left - Search Controls */}
            <div className="flex items-center w-40">
              <div className="flex items-center bg-secondary-light dark:bg-secondary-dark border border-gray-200 dark:border-gray-600 rounded-lg">
                <div className="flex items-center px-3 py-2 text-gray-400 dark:text-gray-500">
                  <HiOutlineSearch className="w-4 h-4" />
                </div>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={handleSearchChange}
                  placeholder="Search..."
                  className="px-2 py-2 text-sm bg-transparent border-none outline-none focus:ring-0 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 w-32"
                />
                {search && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="flex items-center justify-center px-2 py-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-all duration-200 cursor-pointer z-10"
                  >
                    <HiOutlineX className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            {/* Center - Project Navigation */}
            <div className="flex items-center justify-center flex-1">
              <div className="flex flex-col items-center text-center">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {search ? 'FILTERED PROJECT' : 'PROJECT'}
                </span>
                <h2 className="text-xl font-bold bg-gradient-to-r from-primary-light via-primary-dark to-primary-hover bg-clip-text text-transparent mb-0.5">
                  {projectKeys.length > 0
                    ? currentProjectKey
                    : " - "}
                </h2>

                {/* Compact Navigation with Pagination */}
                <div className="flex items-center justify-center space-x-1">
                  <button
                    onClick={handlePreviousProject}
                    disabled={currentProjectIndex === 0 || projectKeys.length === 0}
                    className={`flex items-center justify-center w-5 h-5 rounded-md ${
                      currentProjectIndex === 0 || projectKeys.length === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500' 
                      : 'bg-primary-light/20 text-primary-light hover:bg-primary-light/30 dark:bg-primary-dark/20 dark:text-primary-dark dark:hover:bg-primary-dark/30'
                    } transition-all`}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                    </svg>
                  </button>

                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300 px-3">
                    {projectKeys.length > 0
                      ? `${currentProjectIndex + 1} of ${projectKeys.length}`
                      : "0 of 0"}
                  </span>

                  <button
                    onClick={handleNextProject}
                    disabled={currentProjectIndex >= projectKeys.length - 1 || projectKeys.length === 0}
                    className={`flex items-center justify-center w-5 h-5 rounded-md ${
                      currentProjectIndex >= projectKeys.length - 1 || projectKeys.length === 0
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500' 
                      : 'bg-primary-light/20 text-primary-light hover:bg-primary-light/30 dark:bg-primary-dark/20 dark:text-primary-dark dark:hover:bg-primary-dark/30'
                    } transition-all`}
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Right - Status Filter */}
            <div className="flex items-center justify-end w-40">
              <SingleSelect
                name="status"
                options={statusOptions}
                placeholder="Select status..."
                onChange={(option) => {
                  setSelectedStatus(option?.value ?? '');
                }}
                defaultValue={statusOptions.find(o => o.value === selectedStatus)}
                width="w-32"
                className="text-sm"
              />
            </div>
          </div>
        </div>

        {/* Team Selection - Fixed height */}
        {projectKeys.length > 0 && (
          <div className="bg-gradient-to-r from-hover-light to-tertiary-light dark:from-hover-dark dark:to-tertiary-dark p-4 border-b border-gray-100 dark:border-gray-700 h-30 flex-shrink-0">
            <div className="flex flex-col h-full">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider flex-shrink-0">{selectedType === 'jira' ? 'Board' : 'Team'}</span>
              <div className="relative overflow-x-auto flex-1 min-h-0">
                <div
                  ref={teamScrollContainerRef}
                  className="flex items-center space-x-2 h-full overflow-x-auto"
                >
                  {teamKeys.map((teamName, idx) => {
                    const isCurrentTeam = idx === currentTeamIndex;

                    return (
                      <button
                        id={`team-${idx}`}
                        key={teamName}
                        className={`px-3 py-2 rounded-md transition-all text-sm ${
                          isCurrentTeam
                          ? 'bg-gradient-to-r from-primary-light to-primary-dark text-white font-medium shadow-md' 
                          : 'bg-secondary-light dark:bg-secondary-dark text-gray-700 dark:text-gray-300 hover:bg-hover-light dark:hover:bg-hover-dark border border-gray-200 dark:border-gray-700 hover:border-primary-light/50 dark:hover:border-primary-dark/50'
                        } whitespace-nowrap flex-shrink-0`}
                        onClick={() => setCurrentTeamIndex(idx)}
                      >
                        {teamName}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sprints Content - Fixed height */}
        {projectKeys.length > 0 && (
          <div className="flex-1 p-5 bg-secondary-light dark:bg-secondary-dark overflow-hidden flex  flex-col min-h-0">
            {currentSprints.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                                 <div className="text-center text-gray-500 dark:text-gray-400">
                   <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 mb-3">
                     <FiCalendar className="w-6 h-6" />
                   </div>
                   <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">No Active Sprints</h3>
                   <p className="text-sm text-gray-500 dark:text-gray-400">
                     This team doesn't have any sprints in the selected status
                   </p>
                 </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4 flex-shrink-0">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Sprints</h3>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {currentSprints.length} sprint{currentSprints.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto">
                  <div className="space-y-3">
                    {currentSprints.map((sprint, index) => (
                      <div
                        key={sprint.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-primary-light/5 to-hover-light dark:from-primary-dark/5 dark:to-hover-dark hover:from-primary-light/10 hover:to-hover-light dark:hover:from-primary-dark/10 dark:hover:to-hover-dark transition-all duration-200 group border border-primary-light/10 dark:border-gray-700 hover:border-primary-light/30 dark:hover:border-primary-dark/30"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-primary-light/80 to-primary-dark/90 text-white text-xs font-medium dark:from-primary-dark/70 dark:to-primary-hover/80 shadow-sm flex-shrink-0">
                            {index + 1}
                          </div>
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-700 dark:text-gray-200 group-hover:text-primary-light dark:group-hover:text-primary-dark transition-colors duration-200">
                              {sprint.name}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <FiCalendar className="w-3 h-3" />
                              {formatDate(sprint.start_date)} - {formatDate(sprint.end_date)}
                            </span>
                            <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1 mt-1">
                              <FiFolder className="w-3 h-3" />
                              {sprint.path}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium border ${statusColors[sprint.status] || 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}>
                            {getStatusDisplay(sprint.status)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
        {projectKeys.length === 0 && (
          <div className="flex-1 p-5 bg-secondary-light dark:bg-secondary-dark flex items-center justify-center">
            <div className="text-center text-gray-500 dark:text-gray-400">
          
                             <div className="text-center text-gray-500 dark:text-gray-400">
                 <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 mb-3">
                   <FiCalendar className="w-6 h-6" />
                 </div>
                 <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">No Sprint Data Available</h3>
                 <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                   No projects or sprints found for the selected integration
                 </p>
                 <p className="text-sm text-gray-400 dark:text-gray-500">
                   Try syncing to refresh your sprint data
                 </p>
               </div>
            </div>
          </div>
        )}

        {/* Footer Controls - Fixed height */}
        <div className="border-t border-gray-200 dark:border-gray-700 py-4 px-5 bg-hover-light dark:bg-hover-dark h-20 flex-shrink-0 mt-auto">
          <div className="w-full flex justify-center items-center h-full">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentTeamIndex(prev => Math.max(0, prev - 1))}
                disabled={currentTeamIndex === 0 || teamKeys.length === 0}
                className={`flex items-center justify-center w-6 h-6 rounded-md transition-all ${
                  currentTeamIndex === 0 || teamKeys.length === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500' 
                  : 'bg-primary-light/20 text-primary-light hover:bg-primary-light/30 dark:bg-primary-dark/20 dark:text-primary-dark dark:hover:bg-primary-dark/30'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                </svg>
              </button>

              <span className="text-sm font-medium text-gray-600 dark:text-gray-300 px-3 py-2  ">
                {teamKeys.length > 0 ? `${currentTeamIndex + 1} of ${teamKeys.length}` : '0 of 0'}
              </span>

              <button
                onClick={() => setCurrentTeamIndex(prev => Math.min(teamKeys.length - 1, prev + 1))}
                disabled={currentTeamIndex >= teamKeys.length - 1 || teamKeys.length === 0}
                className={`flex items-center justify-center w-6 h-6 rounded-md transition-all ${
                  currentTeamIndex >= teamKeys.length - 1 || teamKeys.length === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500' 
                  : 'bg-primary-light/20 text-primary-light hover:bg-primary-light/30 dark:bg-primary-dark/20 dark:text-primary-dark dark:hover:bg-primary-dark/30'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}